import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { getWorkshopContentWithBlocks } from '@/lib/db'
import type { WorkshopContent, WorkshopBlock } from '@/types'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  List,
  Maximize2,
  Clock,
  MessageSquare
} from 'lucide-react'
import WorkshopBlockRenderer from '@/components/workshop/WorkshopBlockRenderer'

const SECTION_COLORS: Record<string, string> = {
  'Calentamiento': 'from-orange-400 to-red-500',
  'Introducción': 'from-blue-400 to-blue-600',
  'Vocabulario': 'from-green-400 to-emerald-600',
  'Saludos': 'from-cyan-400 to-blue-500',
  'Presentarse': 'from-violet-400 to-purple-600',
  'Numeros y verbo etre': 'from-amber-400 to-orange-500',
  'Práctica guiada': 'from-pink-400 to-rose-600',
  'Práctica libre': 'from-fuchsia-400 to-pink-600',
  'Practica integrada': 'from-indigo-400 to-violet-600',
  'Juegos': 'from-yellow-400 to-amber-500',
  'Cierre': 'from-teal-400 to-cyan-600',
  'Bonus': 'from-lime-400 to-green-500',
}

function getSectionGradient(section?: string | null) {
  if (!section) return 'from-blue-500 to-purple-600'
  return SECTION_COLORS[section] || 'from-blue-500 to-purple-600'
}

export default function WorkshopPresenter() {
  const { contentId } = useParams()
  const [content, setContent] = useState<WorkshopContent | null>(null)
  const [blocks, setBlocks] = useState<WorkshopBlock[]>([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showNav, setShowNav] = useState(false)
  const [showNotes, setShowNotes] = useState(false)

  useEffect(() => {
    if (!contentId) return
    async function load() {
      setLoading(true)
      const { data } = await getWorkshopContentWithBlocks(contentId!)
      if (data) {
        setContent(data)
        setBlocks(data.blocks || [])
      }
      setLoading(false)
    }
    load()
  }, [contentId])

  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, blocks.length - 1))
  }, [blocks.length])

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0))
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev() }
      else if (e.key === 'Escape') {
        if (showNav) setShowNav(false)
        else if (showNotes) setShowNotes(false)
        else if (document.fullscreenElement) document.exitFullscreen()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goNext, goPrev, showNav, showNotes])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen()
    else document.exitFullscreen()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-lg text-purple-700 font-medium">Cargando taller...</p>
        </div>
      </div>
    )
  }

  if (!content || blocks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 gap-4">
        <p className="text-xl text-gray-500">Sin bloques para presentar</p>
        <button onClick={() => window.close()} className="px-6 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600">
          Cerrar
        </button>
      </div>
    )
  }

  const currentBlock = blocks[currentIndex]
  const progress = ((currentIndex + 1) / blocks.length) * 100
  const gradient = getSectionGradient(currentBlock.section)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col select-none">
      {/* Barra superior con gradiente */}
      <div className={`bg-gradient-to-r ${gradient} px-4 py-2 flex items-center justify-between shadow-lg z-20`}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.close()}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="hidden sm:block">
            {currentBlock.section && (
              <span className="text-white/90 text-sm font-medium bg-white/20 px-3 py-0.5 rounded-full">
                {currentBlock.section}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 text-white">
          <span className="text-sm font-bold bg-white/20 px-3 py-1 rounded-full">
            {currentIndex + 1} / {blocks.length}
          </span>
          {currentBlock.estimated_minutes && (
            <span className="text-sm flex items-center gap-1 opacity-80">
              <Clock className="w-3.5 h-3.5" />
              {currentBlock.estimated_minutes}min
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {currentBlock.instructions && (
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={`p-1.5 rounded-lg transition-colors ${showNotes ? 'bg-white/30 text-white' : 'text-white/80 hover:text-white hover:bg-white/20'}`}
              title="Notas del profesor"
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowNav(!showNav)}
            className={`p-1.5 rounded-lg transition-colors ${showNav ? 'bg-white/30 text-white' : 'text-white/80 hover:text-white hover:bg-white/20'}`}
            title="Lista de bloques"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            title="Pantalla completa"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="h-1.5 bg-gray-100">
        <div
          className={`h-full bg-gradient-to-r ${gradient} transition-all duration-500 ease-out`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Contenido principal */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Panel lateral de navegación */}
        {showNav && (
          <div
            className="absolute inset-0 z-10 flex"
            role="button"
            tabIndex={0}
            onClick={() => setShowNav(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowNav(false) }}
          >
            <span
              className="w-80 bg-white shadow-2xl overflow-y-auto border-r border-gray-200"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-3 border-b border-gray-100">
                <p className="text-sm font-bold text-gray-700">Bloques del taller</p>
              </div>
              <div className="p-2 space-y-0.5">
                {blocks.map((block, idx) => {
                  const isActive = idx === currentIndex
                  const blockGradient = getSectionGradient(block.section)
                  return (
                    <button
                      key={block.id}
                      onClick={() => { setCurrentIndex(idx); setShowNav(false) }}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-sm flex items-center gap-3 transition-all ${
                        isActive
                          ? `bg-gradient-to-r ${blockGradient} text-white shadow-md`
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`font-mono text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                        isActive ? 'bg-white/30' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="truncate flex-1">{block.title || block.block_type}</span>
                      <span className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-400'}`}>{block.estimated_minutes}m</span>
                    </button>
                  )
                })}
              </div>
            </span>
            <div className="flex-1 bg-black/20" />
          </div>
        )}

        {/* Notas del profesor (barra inferior) */}
        {showNotes && currentBlock.instructions && (
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-amber-50 border-t-2 border-amber-300 p-4 shadow-lg">
            <div className="max-w-3xl mx-auto">
              <p className="text-xs font-bold text-amber-600 mb-1 uppercase tracking-wide">Instrucciones para el profesor</p>
              <p className="text-sm text-amber-900">{currentBlock.instructions}</p>
              {currentBlock.teacher_notes && (
                <p className="text-xs text-amber-700 mt-2 italic">{currentBlock.teacher_notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Área del bloque */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 overflow-hidden" key={currentIndex}>
          <div className="w-full max-w-6xl h-full max-h-full flex items-center justify-center animate-fadeIn">
            <div className="w-full max-h-[calc(100vh-70px)] overflow-hidden">
              <WorkshopBlockRenderer block={currentBlock} />
            </div>
          </div>
        </div>

        {/* Botón izquierdo */}
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className={`absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full shadow-xl transition-all z-10 ${
            currentIndex === 0
              ? 'opacity-0 cursor-default'
              : `bg-gradient-to-r ${gradient} text-white hover:scale-110 hover:shadow-2xl`
          }`}
        >
          <ChevronLeft className="w-7 h-7" />
        </button>

        {/* Botón derecho */}
        <button
          onClick={goNext}
          disabled={currentIndex === blocks.length - 1}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full shadow-xl transition-all z-10 ${
            currentIndex === blocks.length - 1
              ? 'opacity-0 cursor-default'
              : `bg-gradient-to-r ${gradient} text-white hover:scale-110 hover:shadow-2xl`
          }`}
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      </div>
    </div>
  )
}
